import * as React from "react";
import Column, { AttributeType, Enum } from "./commons/components/pagination/Column";
import Page from "./commons/components/pagination/Page";
import PaginationBar from "./commons/components/pagination/PaginationBar";
import Sort from "./commons/components/pagination/Sort";
import { Direction } from "./commons/components/pagination/Direction";
import StringFilterView from "./commons/components/pagination/StringFilterView";
import EnumFilterView from "./commons/components/pagination/EnumFilterView";
import { IFilter } from "./commons/components/pagination/filters/IFilter";
import FilterQuery from "./commons/components/pagination/queries/FilterQuery";
import StringFilter from "./commons/components/pagination/filters/StringFilter";
import ArrayFilter from "./commons/components/pagination/filters/ArrayFilter";
import EnumFilter from "./commons/components/pagination/filters/EnumFilter";
import sfetch from "./commons/utils/super-fetch";
import PhoneNumberFilter from "./commons/components/pagination/filters/PhoneNumberFilter";
import { createRoot } from "react-dom/client";
import { dateWithOptionalTime } from "./commons/utils/date";

interface PaginatedListProps {
  initialPage: Page<any>;
  initialSort?: Sort;
  columns: Column[];
  itemKey: string | Getter;
  fetchUrl: string;
  emptyListMessage: string;
  rowOnClick?: (item: any) => any;
  rowClasses?: (item: any) => string;
  onUpdate?: () => any;
  additionalFilterQueries?: FilterQuery[];
  classNames?: string;
  disableFiltering?: boolean;
  onFiltersQueryChange?: (query: string) => void;
  onElementsCountChange?: (count: number) => void;
  allowItemsDragNDrop?: boolean;
  onRowDrop?: (item: any, startIndex: number, endIndex: number) => void;
}

interface PaginatedListState {
  showFilterBar: boolean;
  page: Page<any>;
  sort: Sort;
  filters: { [key: string]: IFilter };
  loading?: boolean;
  errorMessage?: string;
  currentDraggedIndex?: number;
  currentDragOverIndex?: number;
}

type Getter = (x: any) => any;

export default class PaginatedList extends React.Component<PaginatedListProps, PaginatedListState> {
  constructor(props: PaginatedListProps) {
    super(props);
    this.state = {
      page: this.props.initialPage,
      sort: this.props.initialSort,
      showFilterBar: false,
      filters: {},
    };
    this.getSortIcon = this.getSortIcon.bind(this);

    this.props.columns
      .filter((c) => !c.renderer)
      .forEach((c) => {
        c.renderer = this.getRenderer(c);
      });
  }

  private getRenderer(column: Column) {
    switch (column.attributeType) {
      case AttributeType.DATE:
        return (rowData) =>
          dateWithOptionalTime(this.getValue(rowData, column.path), true);
      default:
        return (rowData) => this.getValue(rowData, column.path);
    }
  }

  private lastFetch: Promise<any>;

  render() {
    const numberOfFilters = Object.values(this.state.filters).length;
    const page = this.state.page;
    const hasContent = page.content.length || numberOfFilters;

    return (
      <>
        {!hasContent ? (
          <div className={`table empty ${this.props.classNames || ""}`}>
            {this.props.emptyListMessage}
          </div>
        ) : (
          <>
            {this.state.errorMessage && (
              <div className="table-error h-flex centered">
                <div className="message danger">{this.state.errorMessage}</div>
              </div>
            )}
            {!this.props.disableFiltering && (
              <div className="filter-ctrl h-flex space-between">
                {this.getFilterControls(numberOfFilters)}
                <div className="h-flex with-margins align-center">
                  <button
                    className="button-secondary small refresh"
                    onClick={() => this.refreshCurrentPage()}
                  >
                    <i className="ic_refresh" />
                    <span>{i18n["Global.Refresh"]}</span>
                  </button>
                  <PaginationBar
                    changePage={(pageNumber) => this.fetchData(pageNumber)}
                    page={page}
                  />
                </div>
              </div>
            )}
            <table className={`table ${this.props.classNames || ""}`}>
              <colgroup>
                {this.props.columns.map((column) => (
                  <col key={column.path} className={this.columnClasses(column)} />
                ))}
              </colgroup>
              <thead>
              <tr>
                {this.props.columns.map((column) => (
                  <th
                    key={column.path}
                    className={(column.sortable ? "clickable " : "") + this.columnClasses(column)}
                    onClick={() => this.sort(column)}
                  >
                    <span>{column.title}</span>
                    {this.getSortIcon(column)}
                  </th>
                ))}
              </tr>
              {!this.props.disableFiltering ? this.getFilterRow() : null}
              </thead>

              <tbody className={this.state.loading ? "disabled" : ""}>
              {page.content.length ? (
                page.content.map((item, index) => (
                  <React.Fragment key={this.getValue(item, this.props.itemKey)}>
                    {this.props.allowItemsDragNDrop &&
                      this.state.currentDragOverIndex === index && (
                        <tr className="dropzone">
                          <td></td>
                          <td></td>
                        </tr>
                      )}
                    <tr
                      className={
                        (this.props.rowOnClick ? "clickable" : "") +
                        " " +
                        (this.props.rowClasses ? this.props.rowClasses(item) : "") +
                        (this.state.currentDraggedIndex === index ? " dragged" : "")
                      }
                      key={this.getValue(item, this.props.itemKey)}
                      onClick={() => this.props.rowOnClick && this.props.rowOnClick(item)}
                      data-test={this.getValue(item, this.props.itemKey)}
                      draggable={this.props.allowItemsDragNDrop}
                      onDragStart={(_) => this.onDragStart(index)}
                      onDragEnter={(_) => this.onDragEnter(index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnd={(e) => this.onDrop(e, item)}
                    >
                      {this.props.columns.map((column) => (
                        <td key={column.path} className={this.columnClasses(column)}>
                          {column.renderer(item)}
                        </td>
                      ))}
                    </tr>
                    {this.props.allowItemsDragNDrop &&
                      index === page.content.length - 1 &&
                      (this.state.currentDragOverIndex === index ||
                        this.state.currentDragOverIndex === index + 1) && (
                        <tr className="dropzone" onDragEnter={(_) => this.onDragEnter(index + 1)}>
                          <td></td>
                          <td></td>
                        </tr>
                      )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={this.props.columns.length} style={{ textAlign: "center" }}>
                    {i18n["PaginatedList.NoResult"]}
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </>
        )}
      </>
    );
  }

  columnClasses(column: Column) {
    let classes = `attr-${column.attribute} path-${column.path}`;
    return column.classNames ? `${column.classNames} ${classes}` : classes;
  }

  componentDidUpdate(
    prevProps: Readonly<PaginatedListProps>,
    prevState: Readonly<PaginatedListState>,
    snapshot?: any
  ) {
    this.props.onUpdate && this.props.onUpdate();
    if (this.state.showFilterBar && !prevState.showFilterBar) {
      const firstInput: HTMLElement = document.querySelector(".filter-bar input");
      firstInput && firstInput.focus();
    }
  }

  refreshCurrentPage() {
    this.fetchData(this.state.page.number);
  }

  componentDidMount() {
    this.cleanTagFilter();
    history.replaceState({ queryString: document.location.search }, "");
    this.onPopState = this.onPopState.bind(this);
    window.addEventListener("popstate", this.onPopState);
    this.setConfigFromQueryString(document.location.search, false);
  }

  componentWillUnmount(): void {
    window.removeEventListener("popstate", this.onPopState);
  }

  private cleanTagFilter() {
    const tagsColumn = this.props.columns.find((column) => column.attribute === "PATIENT_TAGS");
    if (tagsColumn) {
      const availableTags = Object.keys((tagsColumn as Enum & Column).values);
      const patientTagsSearch = new URLSearchParams(document.location.search)
        .getAll("q")
        .find((param) => param.startsWith("PATIENT_TAGS"));
      if (patientTagsSearch) {
        const newFilter = patientTagsSearch
          .split("||")
          .filter((t) => availableTags.includes(t.replace("PATIENT_TAGS;ac;", "")))
          .join("||");
        const newSearch = document.location.search.replace(
          "q=" + encodeURIComponent(patientTagsSearch),
          newFilter ? "q=" + encodeURIComponent(newFilter) : ""
        );
        if (newSearch !== document.location.search) {
          document.location.search = newSearch;
        }
      }
    }
  }

  private onPopState(event) {
    if (event.state) {
      this.setConfigFromQueryString(event.state.queryString, true);
    }
  }

  private setConfigFromQueryString(queryString: string, refresh: boolean) {
    const params = new URLSearchParams(queryString);
    const page = params.get("p") ? Number(params.get("p")) : 0;
    const sort = this.buildSort(params.get("s"));
    const filters = this.buildFilters(params.getAll("q"));
    this.props.onFiltersQueryChange && this.props.onFiltersQueryChange(queryString);
    this.setState({ sort, filters }, () => refresh && this.fetchData(page, true));
  }

  private buildFilters(stringFilters?: String[]): { [key: string]: IFilter } | undefined {
    if (stringFilters) {
      const filters = stringFilters
        .map((filter) => filter.split("||"))
        .reduce((acc, val) => acc.concat(val), []) // Flatten
        .map((filter) => filter.split(";"))
        .filter(
          (filter) => filter.length == 3 && this.props.columns.find((c) => c.attribute == filter[0])
        )
        .reduce(
          (acc, filter) => (
            (acc[filter[0]] = acc[filter[0]] || []), acc[filter[0]].push(filter), acc
          ),
          {}
        );

      Object.keys(filters).forEach((attribute) => {
        const column = this.props.columns.find((c) => c.attribute == attribute);
        switch (column.attributeType) {
          case AttributeType.STRING:
            filters[attribute] = new StringFilter(attribute, filters[attribute][0][2]);
            break;
          case AttributeType.PHONE_NUMBER:
            filters[attribute] = new PhoneNumberFilter(attribute, filters[attribute][0][2]);
            break;
          case AttributeType.ENUM:
            filters[attribute] = new EnumFilter(
              attribute,
              filters[attribute].map((it) => it[2])
            );
            break;
          case AttributeType.STRING_ARRAY:
          case AttributeType.UUID_ARRAY:
            filters[attribute] = new ArrayFilter(
              attribute,
              filters[attribute].map((it) => it[2])
            );
            break;
        }
      });
      return filters;
    }
    return;
  }

  private buildSort(sort: String): Sort | undefined {
    if (sort) {
      const sortSplit = sort.split(";");
      if (sortSplit.length == 2) {
        return {
          attribute: sortSplit[0],
          direction: sortSplit[1] as Direction,
        };
      }
    }
    return;
  }

  private handleFilterChange(query: string, totalElements: number) {
    this.props.onFiltersQueryChange && this.props.onFiltersQueryChange(query);
    this.props.onElementsCountChange && this.props.onElementsCountChange(totalElements);
  }

  private fetchData(page?: number, afterPopState?: boolean) {
    let params = [];
    if (page) params.push(`p=${page}`);
    if (this.state.sort) params.push(`s=${this.state.sort.attribute};${this.state.sort.direction}`);
    Object.values(this.state.filters)
      .map((it) => it.toQuery())
      .map((it) => it.buildQueryString())
      .filter((x) => x)
      .forEach((it) => params.push(`q=${encodeURIComponent(it)}`));
    const queryString = params.length ? "?" + params.join("&") : "";
    if (this.props.additionalFilterQueries)
      this.props.additionalFilterQueries.forEach((it) =>
        params.push(`q=${encodeURIComponent(it.buildQueryString())}`)
      );
    const fullQueryString = params.length ? "?" + params.join("&") : "";
    this.setState({ loading: true, errorMessage: null });
    if (!afterPopState) {
      history.pushState(
        { queryString },
        "",
        document.location.origin + document.location.pathname + queryString
      );
    }

    const url = this.props.fetchUrl + fullQueryString;
    const req = sfetch(url, { method: "GET" });
    this.lastFetch = req;
    req
      .then((res) => {
        if (res.ok) return res.json();
        else throw res;
      })
      .then((page: Page<any>) => {
        this.handleFilterChange(fullQueryString, page.totalElements);
        if (req === this.lastFetch) {
          this.setState({ loading: false, page });
        }
      })
      .catch((res) => {
        this.setState({
          loading: false,
          errorMessage: i18n["Global.TechnicalError"],
        });
      });
  }

  private getValue(row, propertyGetter: string | Getter) {
    if (typeof propertyGetter === "string") {
      return propertyGetter
        .split(".")
        .reduce(
          (value, propName) => (value === undefined || value === null ? value : value[propName]),
          row,
        );
    } else {
      return propertyGetter(row);
    }
  }

  private sort(column: Column) {
    if (column.sortable) {
      let sort: Sort;
      if (
        this.state.sort &&
        this.state.sort.attribute === column.attribute &&
        this.state.sort.direction === Direction.ASC
      ) {
        sort = { attribute: column.attribute, direction: Direction.DESC };
      } else {
        sort = { attribute: column.attribute, direction: Direction.ASC };
      }
      this.setState({ sort }, () => this.fetchData(0));
    }
  }

  private getSortIcon(column: Column) {
    if (column.sortable) {
      let iconClass = "ic_sort";
      if (this.state.sort) {
        if (this.state.sort.attribute === column.attribute) {
          if (this.state.sort.direction === Direction.ASC) {
            iconClass = "ic_sort_asc";
          } else if (this.state.sort.direction === Direction.DESC) {
            iconClass = "ic_sort_desc";
          }
        }
      }
      return <i className={iconClass} />;
    }
    return null;
  }

  private getFilterControls(numberOfFilters: number) {
    return (
      <div className="with-margins">
        <button
          className="button-secondary with-margins"
          onClick={() => this.setState({ showFilterBar: !this.state.showFilterBar })}
        >
          <i className="ic_filter" />
          <span>{i18n["PaginatedList.Action.Filters"]}</span>
          {numberOfFilters ? (
            <div className="label primary">
              <span>{numberOfFilters}</span>
            </div>
          ) : null}
        </button>
        {numberOfFilters ? (
          <span
            className="link"
            onClick={() => this.setState({ filters: {} }, () => this.fetchData(0))}
          >
            {i18n["PaginatedList.Action.ClearFilters"]}
          </span>
        ) : null}
      </div>
    );
  }

  private getFilterRow() {
    return (
      <tr className={"filter-bar" + (this.state.showFilterBar ? "" : " none")}>
        {this.props.columns.map((column) => (
          <th key={column.path} className={this.columnClasses(column)}>
            {this.getFilter(column)}
          </th>
        ))}
      </tr>
    );
  }

  private getFilter(column: Column) {
    if (!column.filterPlaceholder) {
      return null;
    }
    const filter = this.state.filters[column.attribute];
    switch (column.attributeType) {
      case AttributeType.STRING:
        return (
          <StringFilterView
            fieldKey={column.attribute}
            placeholder={column.filterPlaceholder}
            onChange={this.onFilterChange}
            filter={filter as StringFilter}
            filterType={StringFilter}
            filterMinimumLength={column.filterMinimumLength}
          />
        );
      case AttributeType.PHONE_NUMBER:
        return (
          <StringFilterView
            fieldKey={column.attribute}
            placeholder={column.filterPlaceholder}
            onChange={this.onFilterChange}
            filter={filter as PhoneNumberFilter}
            filterType={PhoneNumberFilter}
          />
        );
      case AttributeType.ENUM:
        return (
          <EnumFilterView
            fieldKey={column.attribute}
            onChange={this.onFilterChange}
            enumValues={(column as Enum & Column).values}
            filter={filter as EnumFilter}
            filterType={EnumFilter}
            placeholder={column.filterPlaceholder}
          />
        );
      case AttributeType.STRING_ARRAY:
      case AttributeType.UUID_ARRAY:
        return (
          <EnumFilterView
            fieldKey={column.attribute}
            onChange={this.onFilterChange}
            enumValues={(column as Enum & Column).values}
            filter={filter as ArrayFilter}
            filterType={ArrayFilter}
            placeholder={column.filterPlaceholder}
          />
        );
    }
  }

  private onFilterChange = (filterId: string, filter?: IFilter) => {
    const filters = this.state.filters;
    if (filter) {
      filters[filterId] = filter;
    } else {
      delete filters[filterId];
    }
    this.setState({ filters }, () => this.fetchData(0));
  };

  private onDragStart(position: number) {
    this.setState({ currentDraggedIndex: position });
  }

  private onDragEnter(position: number) {
    this.setState({ currentDragOverIndex: position });
  }

  private onDrop(e: React.DragEvent, item: any) {
    e.preventDefault();
    if (this.props.onRowDrop) {
      const startDnDIndex = this.state.currentDraggedIndex;
      const endDnDIndex = this.state.currentDragOverIndex;
      this.props.onRowDrop(item, startDnDIndex, endDnDIndex);
    }
    this.setState({ currentDraggedIndex: null });
    this.setState({ currentDragOverIndex: null });
  }

  static loadComponent(elementId: string, props: PaginatedListProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<PaginatedList {...props} />);
  }
}
